<template>
  <div class="bulk-order-header__container" @touchstart="touchHeader">
    <div class="bulk-order-header__content">
      <h1 class="bulk-order-header__nav--logo">
        <img v-lazy="logoImage" alt="logo" class="giftee-logo" />
      </h1>
      <div class="bulk-order-header__nav--search">
        <div class="bulk-order-header__nav--search--wrap">
          <SearchInputForm
            :value="keyword"
            placeholder="キーワード検索 （例）コーヒー、アイス"
            @input-search-keyword="onInputSearchText"
            @on-focus="onFocusSearchText"
            @on-blur="onBlurSearchText"
            @clicked-search-keyword="onClickSearchKeyword"
            @clicked-clear-keyword="onClickClearKeyword"
          />
          <button
            v-show="canShowSuggestedKeywords"
            class="suggest__cancel"
            @click="hideSuggestKeyword"
          >
            キャンセル
          </button>
        </div>
        <SuggestListForPc
          v-show="isPc"
          :can-show-suggested-keywords="canShowSuggestedKeywords"
          :suggested-keywords="suggestedItemKeywords"
          @click-suggested-keyword="onClickSuggestedKeyword"
        />
      </div>
    </div>
    <SuggestListForSp
      v-show="isSp"
      :can-show-suggested-keywords="canShowSuggestedKeywords"
      :suggested-keywords="suggestedItemKeywords"
      @click-suggested-keyword="onClickSuggestedKeyword"
    />
  </div>
</template>
<script>
import qs from 'qs'
import url from 'url'
import SearchInputForm from '../../molecules/input_forms/SearchInputForm.vue'
import logo from '../../../../../images/logo.svg'
import SuggestListForPc from '../../molecules/suggest_list/SuggestListForPc.vue'
import SuggestListForSp from '../../molecules/suggest_list/SuggestListForSp.vue'
import FetchSuggestedItemKeywordsQuery from '../../../../../graphqls/public/queries/fetch_suggested_item_keywords'
import * as Sentry from '@sentry/browser'
import { debounce } from 'lodash'

const SUGGEST_DEBOUNCE_TIME = 400
const SUGGESTLIST_CLOSE_TIME = 200
const SP_SIZE_WIDTH = 768

export default {
  components: {
    SuggestListForPc,
    SuggestListForSp,
    SearchInputForm
  },
  props: {
    bulkPersonalOrderSessionId: {
      type: Number,
      required: true
    },
    giftCardSessionToken: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      modalText: {
        title: '作成中のギフトカードがあります',
        caption:
          '作成できるカードは１つまでです。\n内容を破棄して新規作成しますか?',
        primaryButton: '新規作成する',
        secondaryButton: 'カードを確認する',
        cancelButton: '閉じる'
      },
      logoImage: '',
      keyword: '',
      sortKey: 'recommended',
      showSuggestKeywords: false,
      suggestedItemKeywords: [],
      lowerBudget: null,
      higherBudget: null,
      categoryIds: [],
      sceneIds: [],
      receivingKindIds: []
    }
  },
  computed: {
    isKeywordInputted() {
      return this.keyword.trim().length !== 0
    },
    canShowSuggestedKeywords() {
      return this.suggestedItemKeywords.length > 0 && this.showSuggestKeywords
    }
  },
  mounted() {
    this.logoImage = logo
    this.setSearchParamsFromUrl() // URLのパラメータから現状の検索条件の取得
  },
  methods: {
    isSp() {
      return window.innerWidth <= SP_SIZE_WIDTH
    },
    isPc() {
      return window.innerWidth > SP_SIZE_WIDTH
    },
    touchHeader() {
      // 以下の場合、何かしら画面上を触ったらソフトウェアキーボードを非表示するために、blurを行う
      // - 「SPサイズの場合」 かつ 「サジェストを表示している場合」
      if (this.canShowSuggestedKeywords && this.isSp()) {
        document.activeElement.blur()
      }
    },
    setSearchParamsFromUrl(isUpdateKeyword = true) {
      const urlQuery = url.parse(location.href).query
      if (!urlQuery) {
        return
      }
      const parsed = qs.parse(urlQuery)
      this.sortKey = parsed.sort_key ? parsed.sort_key : 'recommended'

      if (isUpdateKeyword) {
        this.keyword = parsed.keyword ? parsed.keyword : ''
      }
      this.lowerBudget = parsed.lower_budget
        ? Number(parsed.lower_budget)
        : null
      this.higherBudget = parsed.higher_budget
        ? Number(parsed.higher_budget)
        : null
      this.categoryIds = parsed.category_ids
        ? parsed.category_ids.map(categoryId => Number(categoryId))
        : []
      this.sceneIds = parsed.scene_ids
        ? parsed.scene_ids.map(sceneId => Number(sceneId))
        : []
      this.receivingKindIds =
        parsed.item_receiving_kinds && parsed.item_receiving_kinds
          ? parsed.item_receiving_kinds.map(itemReceivingKindId =>
              Number(itemReceivingKindId)
            )
          : []
    },
    onInputSearchText(keyword) {
      // 前と同じキーワードの場合は、スキップ
      if (this.keyword == keyword) return

      this.keyword = keyword
      if (this.keyword.length === 0) {
        this.suggestedItemKeywords = []
        return
      }

      // サジェストリストの取得
      this.fetchSuggestKeyword(this.keyword)
    },
    onFocusSearchText() {
      this.showSuggestKeywords = true
    },
    onBlurSearchText() {
      // PCサイズの場合は、検索窓のBlur時にサジェストを非表示に
      if (this.isPc()) {
        // 即座に実行すると、サジェストの結果をクリック前に非表示になってしまうため、非表示するのを少し待つように
        setTimeout(() => {
          this.showSuggestKeywords = false
        }, SUGGESTLIST_CLOSE_TIME)
      }
    },
    search() {
      // 絞り込みを変更したときにURLのパラメータが変更される。そのため検索前に再度URLのパラメータから検索条件を取得する
      // また、検索時は入力中のキーワードを利用するため、キーワードはURL中のものを利用しないようにfalseを指定
      this.setSearchParamsFromUrl(false)
      const params = this.createParamsObjectFromData()
      const paramsString = qs.stringify(params, { arrayFormat: 'brackets' })
      window.location.href = `/bulk_order/items?${paramsString}`
    },
    onClickSearchKeyword() {
      this.search()
      this.dispatchDoSearchEvent({ via: 'search_input' })
    },
    dispatchDoSearchEvent({ via }) {
      dataLayer.push({
        event: 'do__search_bulk',
        sort_key: this.sortKey,
        lower_budget: this.lowerBudget,
        higher_budget: this.higherBudget,
        scene_id: this.sceneIds.length > 0 ? this.sceneIds.join(',') : '',
        category_id:
          this.categoryIds.length > 0 ? this.categoryIds.join(',') : '',
        item_receiving_kinds:
          this.receivingKindIds.length > 0
            ? this.receivingKindIds.join(',')
            : '',
        keyword: this.keyword,
        via,
        bulk_personal_order_session_id: this.bulkPersonalOrderSessionId
      })
    },
    createParamsObjectFromData() {
      const token = { token: this.giftCardSessionToken }
      const keyword = this.keyword ? { keyword: this.keyword } : {}
      const sortKey =
        this.sortKey !== 'recommended' ? { sort_key: this.sortKey } : {}
      const lowerBudget = this.lowerBudget
        ? { lower_budget: this.lowerBudget }
        : {}
      const higherBudget = this.higherBudget
        ? { higher_budget: this.higherBudget }
        : {}
      const categoryIds = this.categoryIds
        ? { category_ids: this.categoryIds }
        : {}
      const sceneIds = this.sceneIds ? { scene_ids: this.sceneIds } : {}
      const itemReceivingKinds = this.receivingKindIds
        ? { item_receiving_kinds: this.receivingKindIds }
        : {}
      return Object.assign(
        {},
        sortKey,
        lowerBudget,
        higherBudget,
        categoryIds,
        sceneIds,
        itemReceivingKinds,
        keyword,
        token
      )
    },
    onClickClearKeyword() {
      this.keyword = ''
      this.suggestedItemKeywords = []
      this.search()
      this.dispatchDoSearchEvent({ via: 'item_list' })
    },
    fetchSuggestKeyword: debounce(async function(keyword) {
      // リクエスト前にすでにキーワードがない場合はリクエストしない
      if (!this.isKeywordInputted) return

      try {
        const response = await this.$apollo.provider.clients.publicClient.query(
          {
            query: FetchSuggestedItemKeywordsQuery,
            variables: {
              keyword: keyword
            }
          }
        )
        if (this.isKeywordInputted) {
          this.suggestedItemKeywords = response.data.suggestedItemKeywords
        } else {
          // リクエストが返ってきた時に、キーワードがない場合はサジェスト内容を空にする
          this.suggestedItemKeywords = []
        }
      } catch (error) {
        Sentry.captureException(error)
      }
    }, SUGGEST_DEBOUNCE_TIME),
    onClickSuggestedKeyword(keyword) {
      this.keyword = keyword
      this.onClickSearchKeyword()
      this.suggestedItemKeywords = []
    },
    hideSuggestKeyword() {
      this.showSuggestKeywords = false
    }
  }
}
</script>

<style lang="scss" scoped>
.bulk-order-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: $white;
  // ヘッダを固定して常にコンテンツの上に乗せるため100を指定。

  &__content {
    max-width: $max-contents-width;
    display: flex;
    justify-items: center;
    align-items: center;
    flex-direction: column;
    gap: 16px;
    margin: auto;
  }

  &__container {
    border-bottom: solid 1px $gray-2;
    padding: 27px 16px;
    @media all and (max-width: ($base-contents-width - 1)) {
      padding: 16px;
    }

    .giftee-logo {
      height: 28px;
      width: 80px;
    }
  }

  &__nav {
    &--logo {
      height: 28px;
      justify-self: left;

      a {
        display: inline-block;
      }
    }

    &--search {
      width: 100%;
      position: relative;
      &--wrap {
        display: flex;
        gap: 16px;
        .suggest__cancel {
          display: block;
          white-space: nowrap;
        }
      }
    }
  }

  @media all and (min-width: $base-contents-width) {
    &__content {
      display: flex;
      justify-items: center;
      align-items: center;
      flex-direction: row;
      gap: 56px;
    }
  }
}
</style>
